import {
  posthogEvents,
  queryParams,
  relewiseFallback
} from '@/shared/constants/constants';
import { posthogLog } from '@/shared/pinia/modules/posthog/logger';
import { parseResultsCount } from '@/shared/tracking/parse-results-count';
import {
  isRelewiseFailureReason,
  type RelewiseFailureReason
} from '@/shared/utils/relewise-fallback-reason';

/**
 * Which engine served the current search results.
 * Set by the caller according to which engine's code path runs, which follows
 * `window.Hobbii.flags.enableRelewiseSearch` (theme setting + market metafield,
 * decided in Liquid), NOT the URL query param - the rw-term/q param is
 * unreliable as an engine signal.
 */
export type SearchEngine = 'native' | 'relewise';

/**
 * Which search surface the event came from.
 * - `results_page`: the dedicated /search results page.
 * - `predictive`: the type-ahead dropdown ("preview bar") in the header.
 */
export type SearchSurface = 'results_page' | 'predictive';

export const getSearchQuery = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get(queryParams.RELEWISE_SEARCH_QUERY) ||
    params.get(queryParams.SEARCH_QUERY)
  );
};

const getNativeFallbackReason = (
  surface: SearchSurface
): RelewiseFailureReason | null => {
  const value =
    surface === 'results_page'
      ? new URLSearchParams(window.location.search).get(
          queryParams.RELEWISE_FALLBACK_REASON
        )
      : document.getElementById(relewiseFallback.NATIVE_HEADER_ID)?.dataset[
          relewiseFallback.FALLBACK_REASON_ATTRIBUTE
        ];

  return isRelewiseFailureReason(value) ? value : null;
};

type TrackSearchUsedInput = {
  engine: SearchEngine;
  surface: SearchSurface;
  query: string | null;
  resultsCount: number | null;
};

/**
 * Core search_used emitter. Callers pass explicit inputs so the same emitter
 * serves both the results page (query from URL) and the predictive dropdowns
 * (query from the live input - there is no URL term while typing).
 *
 * Early-returns when there is no query.
 */
export const trackSearchUsed = ({
  engine,
  surface,
  query,
  resultsCount
}: TrackSearchUsedInput) => {
  if (!query) {
    return;
  }

  const fallbackReason =
    engine === 'native' ? getNativeFallbackReason(surface) : null;

  const data = {
    results_count: resultsCount,
    query,
    engine,
    surface,
    ...(fallbackReason && { fallback: true, fallback_reason: fallbackReason })
  };

  posthogLog('search_used', data);
  window.posthog?.capture(posthogEvents.SEARCH_USED, data);
};

/**
 * Results-page helper: sources `query` from the URL and `resultsCount` from the
 * on-page count element, so results-page callers stay a one-liner. Surface is
 * always `results_page` here.
 */
export const trackResultsPageSearchUsed = (
  resultsElement: HTMLElement | null,
  engine: SearchEngine
) => {
  trackSearchUsed({
    engine,
    surface: 'results_page',
    query: getSearchQuery(),
    resultsCount: parseResultsCount(resultsElement)
  });
};
